import * as XLSX from 'xlsx'
import { WaterlineChild, WaterlineInventoryItem } from '../types'
import { WaterlineTestResult } from '../../../practice/waterline/types'

type practiceExportData = {
  practiceName: string,
  doctorName: string,
  street: string,
  city: string,
  state: string,
  zip: string,
  customerNumber: string,
}

const getReplacementDate = (date: string | null | undefined) => {
  if (date) {
    const replacementDate = new Date(date)
    replacementDate.setFullYear(replacementDate.getFullYear() + 1)
    return replacementDate.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'})
  } else return ''
}

const getExportData = (practiceData: practiceExportData, waterlineTestResults: WaterlineTestResult[]) => {
  const { practiceName, doctorName, street, city, state, zip, customerNumber } = practiceData

  const exportData = waterlineTestResults
    .map(({ test_result_id, location, product, hpc_method, in_office_test, is_pass, sample_number, retest, room, test_date, receive_date, report_date, test_operator, result_description, cfu, pH, TDS, next_steps, addressed_on, addressed_note }) => ({
      'Practice Name': practiceName.trim(),
      'Doctor Name': doctorName.trim(),
      'Address Line 1': street.trim(),
      'City': city.trim(),
      'State': state.trim(),
      'Zip Code': zip.trim(),
      'Customer #': customerNumber.trim(),
      'Test ID': test_result_id,
      'Room/Chair': room ? room.name : location,
      'Daily Treatment Method': product,
      'Test Type': hpc_method,
      'Test Result': is_pass ? 'PASS' : 'FAIL',
      'Sample #': sample_number,
      'Test/Retest': retest ? 'Retest' : 'Test',
      'Test Date': (new Date(test_date ?? '')).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'}),
      'Received Date':(new Date(receive_date ?? '')).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'}),
      'Results Date': (new Date(report_date ?? '')).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'}),
      'Team Member/Lab Tech': test_operator,
      'Description': result_description,
      'CFU/ml': cfu,
      'pH': pH,
      'TDS': TDS,
      'Next Steps': next_steps,
      'Addressed On': addressed_on ? (new Date(addressed_on)).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'}) : '',
      'CAPA': addressed_note,
      'In-Office/Mail-In': in_office_test ? 'In-Office' : 'Mail-In',
    }))

  return exportData
}

const exportAllResults = (corpChildren: WaterlineChild[] | null) => {
  exportResults(corpChildren, 'all-results')
}

const exportResultsByPractice = (corpChildren: WaterlineChild[] | null) => {
  if (corpChildren?.length) {

    const wb = XLSX.utils.book_new()

    for (let customer of corpChildren) {
      if (Number(customer.waterline_test_results?.length) > 0) {
        const sheetName = `${customer.state.substring(0, 2)}_${customer.name.substring(0, 10)}_${customer.customer_number}`
        const customerData = [
          {
            A: customer.name.trim()
          },
          {
            A: customer.doctor_name.trim()
          },
          {
            A: customer.street.trim()
          },
          {
            A: `${customer.city.trim()}, ${customer.state.trim()} ${customer.zip.trim()}`
          },
          {
            A: `Customer #: ${customer.customer_number.trim()}`
          }
        ]

        const formattedTests = customer.waterline_test_results
          ?.sort((a, b) => (new Date(b.test_date ?? '')).getTime() - (new Date(a.test_date ?? '')).getTime())
          .map(({ test_result_id, location, product, hpc_method, in_office_test, is_pass, sample_number, retest, room, test_date, test_operator, result_description, cfu, pH, TDS }) => ({
            'Test ID': test_result_id,
            'Room/Chair': room ? room.name : location,
            'Daily Treatment Method': product,
            'Test Type': hpc_method,
            'Test Result': is_pass ? 'PASS' : 'FAIL',
            'Sample Number': sample_number,
            'Test/Retest': retest ? 'Retest' : 'Test',
            'Test Date': (new Date(test_date ?? '')).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'}),
            'Team Member/Lab Tech': test_operator,
            'Description': result_description,
            'CFU/ml': cfu,
            'pH': pH,
            'TDS': TDS,
            'In-Office/Mail-In': in_office_test ? 'In-Office' : 'Mail-In'
          }))
        
        if (formattedTests) {
          const ws = XLSX.utils.json_to_sheet(customerData, { skipHeader: true })

          XLSX.utils.sheet_add_json(ws, formattedTests, { origin: "A8" })
          XLSX.utils.book_append_sheet(wb, ws, sheetName)
        }
      }
    }

    XLSX.writeFile(wb, 'waterline_results_by_practice.xlsx')
  }
}

const exportLastResults = (corpChildren: WaterlineChild[] | null) => {
  exportResults(corpChildren, 'last-results')
}

const exportResults = (corpChildren: WaterlineChild[] | null, type: string) => {
  if (corpChildren?.length) {

    const testList = corpChildren.flatMap(({ name, doctor_name, street, city, state, zip, customer_number, waterline_test_results }) => {
      if (!waterline_test_results?.length) return []

      const practiceData = {
        practiceName: name,
        doctorName: doctor_name,
        street: street,
        city: city,
        state: state,
        zip: zip,
        customerNumber: customer_number,
      }

      waterline_test_results.sort((a, b) => (new Date(b.test_date ?? '')).getTime() - (new Date(a.test_date ?? '')).getTime())

      let results = waterline_test_results

      if (type === 'last-results') {
        const firstTestDate = waterline_test_results[0].test_date
        results = waterline_test_results.filter(test => test.test_date === firstTestDate)
      }

      return getExportData(practiceData, results)
    })

    if (testList?.length) {
      const fileNameMap = new Map([['all-results',  'waterline_all_results.xlsx'], ['last-results', 'waterline_last_results.xlsx']])
      const fileName = fileNameMap.get(type) ?? 'corp-waterline-export.xlsx'

      const wb = XLSX.utils.book_new()
      const ws = XLSX.utils.json_to_sheet(testList)
  
      XLSX.utils.book_append_sheet(wb, ws, 'Waterline Test Results')
      XLSX.writeFile(wb, fileName)   
    }
  } 
}

const exportInventoryItems = (inventory: WaterlineInventoryItem[]) => {
  const formattedItems = inventory
    .sort((a, b) => (new Date(b.shipped_date ?? 0)).getTime() - (new Date(a.shipped_date ?? 0)).getTime())
    .map((item) => {
      const activatedDate = item.activated_date ? new Date(item.activated_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'}) : ''
      const shippedDate = item.shipped_date ? new Date(item.shipped_date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric'}) : ''

      return {
          Status: item.active ? 'Active' : 'Used',
          Practice: item.childCustomerName,
          'Customer Number': item.customer_number,
          'Item Name': item.name,
          'Item Number': item.item_number,
          'Order Number': item.invoice_number,
          'Room/Chair': item.room && item.room.name,
          'Start Date': item.subcategory?.toLowerCase() === 'treatment' || item.subcategory?.toLowerCase() === 'tablets' ? activatedDate : 'N/A',
          'Install Date': item.subcategory?.toLowerCase() === 'straws' ? activatedDate : 'N/A',
          'Test Date': item.subcategory?.toLowerCase() === 'mail-test' || item.subcategory?.toLowerCase() === 'in-office' ? activatedDate : 'N/A',
          'Ship Date': shippedDate,
          'Replacement Date': item.subcategory?.toLowerCase() === 'straws' ? (activatedDate || getReplacementDate(item.shipped_date)) : 'N/A',
          'Auto-Ship': item.is_tbr ? 'Enrolled' : 'Not Enrolled',
      }
    })

  const ws = XLSX.utils.json_to_sheet(formattedItems)
  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(wb, ws, 'Waterline Inventory')
  XLSX.writeFile(wb, 'waterline_inventory_items.xlsx')
}

export {
  exportAllResults,
  exportResultsByPractice,
  exportLastResults,
  exportInventoryItems,
}
